/**
 * Shared plumbing for mechanic duties (mechanics.ts, spread.ts): the Duty shape, the
 * reaction + reliability gate, and small movement/geometry helpers.
 */
import type { Command, Vec2 } from '@keystone/sim';
import { add, dist, fromAngle, scale } from '../geometry';
import { reactionRoll, rollFor, type BotMemory } from '../memory';
import { simWorld, type DangerArea, type Perception, type RUnit } from '../perception';
import { inDanger } from './dodge';

export interface Duty {
  kind: 'tether' | 'soak' | 'behind' | 'lookAway' | 'kite' | 'explode' | 'isolate' | 'fixate' | 'spread' | 'clear';
  urgent: boolean;
  cmds: Command[];
}

/**
 * A duty is noticed `reactionMs` after it appears, then needs a `dodgeReliability` roll. A failed
 * roll means "late", not "never": it is re-rolled every DUTY_REROLL_MS and latches once it passes.
 * The skill profile adds a per-instance reaction delay and a `missChance` to never handle it.
 */
export const DUTY_REROLL_MS = 700;
export function seen(p: Perception, mem: BotMemory, key: string, since: number): boolean {
  // Skill dial: per-instance reaction delay, and a chance to never handle this instance.
  const rx = reactionRoll(mem, `duty:${key}`);
  if (!rx.ok || p.now < since + rx.at) return false;
  if (mem.rolls.get(`duty:${key}`)?.ok) return true;
  const bucket = Math.floor((p.now - since - rx.at) / DUTY_REROLL_MS);
  const r = rollFor(mem, `duty:${key}:${bucket}`, mem.skill.dodgeReliability);
  if (r.ok) mem.rolls.set(`duty:${key}`, r);
  return r.ok;
}

/** Walk to `dest` (cancelling a cast that can't move); stop when there. */
export function moveTo(p: Perception, mem: BotMemory, dest: Vec2): Command[] {
  const self = p.self;
  if (dist(self.pos, dest) < 0.75) return self.move ? [{ type: 'stop', actorId: self.id }] : [];
  if (self.move?.mode === 'dest' && dist(self.move.dest, dest) < 1) return [];
  const cmds: Command[] = [];
  if (self.cast) {
    const ab = p.view.content.ability(self.cast.abilityId);
    if (!ab?.canCastWhileMoving) cmds.push({ type: 'cancelCast', actorId: self.id });
  }
  cmds.push({ type: 'move', actorId: self.id, dest });
  mem.lastMoveDest = dest;
  return cmds;
}

/** Hold still (already in position). */
export function hold(p: Perception): Command[] {
  return p.self.move ? [{ type: 'stop', actorId: p.self.id }] : [];
}

export const walkable = (p: Perception, at: Vec2): boolean => p.view.queries.spatial.isWalkable(simWorld(p), at);

/** First walkable point at distance `r` from `c`, scanning around angle `a0` (alternating sides). */
export function ringPoint(p: Perception, c: Vec2, a0: number, r: number, dangers: readonly DangerArea[]): Vec2 | null {
  for (let i = 0; i < 16; i++) {
    const k = i === 0 ? 0 : (i % 2 === 1 ? 1 : -1) * Math.ceil(i / 2);
    const q = add(c, scale(fromAngle(a0 + (k * Math.PI) / 8), r));
    if (walkable(p, q) && !inDanger(p, dangers, q, p.self.radius)) return q;
  }
  return null;
}

/**
 * Nearest acceptable point around me: scans rings of growing radius (16 bearings each) and returns
 * the lowest-scoring point that passes `ok` (walkable and out of `dangers` is always required).
 */
export function searchPoint(
  p: Perception,
  dangers: readonly DangerArea[],
  ok: (q: Vec2) => boolean,
  score: (q: Vec2, ring: number, i: number) => number = (_q, _r, i) => i,
  maxR = 30,
): Vec2 | null {
  const good = (q: Vec2): boolean => walkable(p, q) && ok(q) && !inDanger(p, dangers, q, p.self.radius);
  if (good(p.self.pos)) return { ...p.self.pos };
  for (let r = 3; r <= maxR; r += 3) {
    let best: Vec2 | null = null;
    let bestScore = Infinity;
    for (let i = 0; i < 16; i++) {
      const q = add(p.self.pos, scale(fromAngle((i / 16) * 2 * Math.PI), r));
      if (!good(q)) continue;
      const s = score(q, r, i);
      if (s < bestScore) {
        bestScore = s;
        best = q;
      }
    }
    if (best) return best;
  }
  return null;
}

/** A point at least `minDist` from every other party member, near me (closest to `anchor` if given). */
export function awayFromAllies(p: Perception, minDist: number, dangers: readonly DangerArea[], anchor?: Vec2): Vec2 | null {
  const others = p.party.filter((u) => u.id !== p.self.id);
  return searchPoint(
    p,
    dangers,
    (q) => others.every((u) => dist(u.pos, q) >= minDist),
    anchor ? (q) => dist(q, anchor) : undefined,
  );
}

/** Engaged enemies casting an ability with this counter hint (noticed after reactionMs). */
export function castCounters(p: Perception, mem: BotMemory, counter: string): RUnit[] {
  return p.engaged.filter((e) => {
    if (!e.cast || p.now < e.cast.startedAt + mem.skill.reactionMs) return false;
    return p.view.content.enemyAbility(e.cast.abilityId)?.counter?.includes(counter as never) ?? false;
  });
}
